import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './CharacterEditor.css'
import { useCharacter } from "./CharacterProvider";
import CharacterSelector from "./CharacterSelector";
import CharacterForm from "./CharacterForm";
import CharacterSkills from "./CharacterSkills";
import { printCharacterSheet } from "./printCharacterSheet";
import * as api from "./utils-api";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const CharacterEditor = ({token, playerId, staffPermissions}) => {
    const {currentCharacter} = useCharacter();
    const [races, setRaces] = useState([]);
    const [skillsets, setSkillsets] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [saving, setSaving] = useState(false);
    const [autosave, setAutosave] = useState(
        () => localStorage.getItem('autosave') !== 'false'
    );

    const prevCharacterIdRef = useRef(null);
    const autosaveTimerRef = useRef(null);

    useEffect(() => {
        document.title = currentCharacter?.name
            ? `${currentCharacter.name} | Chronicles Character Manager`
            : 'Character Manager';
        return () => { document.title = 'Chronicles'; };
    }, [currentCharacter?.name]);

    api.setToken(token);

    const saveCharacter = useCallback(async (characterData, { silent = false } = {}) => {
        setSaving(true);
        try {
            const payload = { ...characterData, race: characterData.raceId };
            await axios.put(`${API_BASE_URL}/character/${characterData.characterId}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!silent) toast.success("Save successful!");
        } catch {
            toast.error("Failed to save character");
        } finally {
            setTimeout(() => setSaving(false), 500);
        }
    }, [token]);

    // Autosave: fires 1 s after any change to currentCharacter, but not on initial character load/switch.
    useEffect(() => {
        if (!currentCharacter?.characterId) return;

        // Character was switched — record the new ID but don't save.
        if (prevCharacterIdRef.current !== currentCharacter.characterId) {
            prevCharacterIdRef.current = currentCharacter.characterId;
            return;
        }

        if (!autosave) return;

        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            saveCharacter(currentCharacter, { silent: true });
        }, 1000);

        return () => clearTimeout(autosaveTimerRef.current);
    }, [currentCharacter, autosave, saveCharacter]);

    useEffect(() => {
        (async function () {
            setRaces(await api.fetchRaces());
            setSkillsets(await api.fetchSkillsets());
        })();
    }, [token]);

    useEffect(() => {
        async function fetchSkillsForCharacter() {
            if (currentCharacter?.characterId) {
                const fetchedSkills = await api.fetchSkills({ characterId: currentCharacter.characterId });
                setAvailableSkills(fetchedSkills);
            }
        }
        fetchSkillsForCharacter();
        // eslint-disable-next-line
    }, [currentCharacter?.characterId, token]);


    return (
        <div className="character-select-container">
            <h1 className="landing-title">Character Manager</h1>
            <div className="selector-print-row">
                <CharacterSelector token={token} playerId={playerId} staffPermissions={staffPermissions} />
                {currentCharacter && (
                    <>
                        <button
                            className="print-btn"
                            onClick={() => saveCharacter(currentCharacter)}
                            disabled={saving || autosave}
                        >
                            {saving && !autosave ? 'Saving…' : 'Save Character'}
                        </button>
                        <button
                            className={`print-btn${autosave ? ' print-btn--on' : ''}`}
                            onClick={() => setAutosave(a => {
                                localStorage.setItem('autosave', String(!a));
                                return !a;
                            })}
                        >
                            Autosave: {autosave ? 'ON' : 'OFF'}
                        </button>
                        <button
                            className="print-btn"
                            onClick={() => {
                                printCharacterSheet({
                                    character: currentCharacter,
                                    races,
                                    skillsets,
                                    availableSkills,
                                });
                            }}
                        >
                            🖨️ Print Character Sheet
                        </button>
                    </>
                )}
            </div>

            {currentCharacter && (
                <>
                    <CharacterForm
                        races={races}
                        skillsets={skillsets}
                        availableSkills={availableSkills}
                        staffPermissions={staffPermissions}
                    />
                    <CharacterSkills
                        token={token}
                        availableSkills={availableSkills}
                    />
                </>
            )}

        </div>
    );
}

export default CharacterEditor;
